/** @jsx jsx */
import { type SerializedStyles, jsx } from '@emotion/react';

import { useMemo } from 'react';

import type { AiCoach_Action } from '__generated__/graphql-types';

import { get as getUser } from 'js/lib/user';

import EmptyCoachState from 'bundles/admin-dashboard/components/EmptyCoachState';
import { CoachChat } from 'bundles/ai-coach-platform/components/patterns';
import { sendMessage as sendMessagePlatform } from 'bundles/ai-coach-platform/components/patterns/chat/store/dispatcher';
import type { QuickMessage } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import { firstNameFromFullName } from 'bundles/ai-coach-platform/utils/nameParser';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';
import { useEnterpriseAdminCoach } from 'bundles/enterprise-admin/EnterpriseAdminCoachContext';
import { useEnterpriseAdminContext } from 'bundles/enterprise-admin/EnterpriseAdminContext';
import type { ThirdPartyOrganization } from 'bundles/enterprise-admin/types';

import _t from 'i18n!nls/admin-dashboard';

type Props = {
  thirdPartyOrganization: ThirdPartyOrganization;
  toggleCoach: () => void;
  style?: SerializedStyles;
};

const COACH_MODE_ID = 'ENTERPRISE_ADMIN';

const getJustStartedQuickMessages = (): QuickMessage[] => {
  return [
    {
      message: _t('How can I find Skill breakdown analysis?'),
      action: 'ASSISTANT' as AiCoach_Action,
    },
    {
      message: _t('How do license usage and expiration work?'),
      action: 'ASSISTANT' as AiCoach_Action,
    },
    {
      message: _t('Why can’t a learner enroll in a course/program?'),
      action: 'ASSISTANT' as AiCoach_Action,
    },
  ];
};

const AdminCoach = ({ thirdPartyOrganization, toggleCoach, style }: Props) => {
  const { currentTimePeriod, contractIds, programIds } = useEnterpriseAdminCoach();
  const { adminRoleType } = useEnterpriseAdminContext();
  const justStartedQuickMessages = getJustStartedQuickMessages();
  const { fullName: userName } = getUser();
  const userFirstName = userName ? firstNameFromFullName(userName) : null;
  const displayTitle = _t('Hi #{userFirstName}!', { userFirstName: userFirstName || 'Admin' });
  const displaySubtitle = _t('How can I help you manage your organization today?');

  const contextConfig = useMemo(
    () => ({
      config: {
        coachModeId: COACH_MODE_ID,
        coachActionId: 'ASSISTANT',
      },
      additionalContext: [
        {
          contextKey: 'additionalContext.enterprise_organization_id',
          contextValue: thirdPartyOrganization.id,
        },
        {
          contextKey: 'additionalContext.enterprise_organization_slug',
          contextValue: thirdPartyOrganization.slug,
        },
        {
          contextKey: 'additionalContext.time_period_filter_enterprise_admin_home',
          contextValue: currentTimePeriod,
        },
        {
          contextKey: 'additionalContext.contracts_filter_enterprise_admin_home',
          contextValue: contractIds.join(','),
        },
        {
          contextKey: 'additionalContext.programs_filter_enterprise_admin_home',
          contextValue: programIds.join(','),
        },
        {
          contextKey: 'additionalContext.admin_role_type',
          contextValue: adminRoleType ?? '',
        },
      ],
      courseraContext: {},
    }),
    [thirdPartyOrganization.id, thirdPartyOrganization.slug, currentTimePeriod, contractIds, programIds, adminRoleType]
  );

  return (
    <ErrorBoundaryWithTagging componentName="EnterpriseAdminCoach">
      <CoachChat
        // Key prop forces complete re-render when filters change, ensuring fresh context
        key={`coach-chat-${currentTimePeriod}-${contractIds.join(',')}-${programIds.join(',')}-${adminRoleType || ''}`}
        mode={COACH_MODE_ID}
        showSettings={true}
        enableRichInputField={true}
        contextConfig={contextConfig}
        trackingData={{
          mode: COACH_MODE_ID,
          location: 'toolbar',
        }}
        emptyState={
          <EmptyCoachState
            title={displayTitle}
            subtitle={displaySubtitle}
            sendMessage={(message: string, action: string) => {
              sendMessagePlatform({
                input: {
                  message,
                  additionalContext: contextConfig.additionalContext,
                  courseraContext: contextConfig.courseraContext,
                  config: {
                    coachActionId: action,
                    coachModeId: COACH_MODE_ID,
                  },
                },
                enableTextStreaming: true,
                enableContinuousChat: true,
              });
            }}
            quickMessages={justStartedQuickMessages}
          />
        }
        enableIntentionRouting={true}
        enableTextStreaming={true}
        enableFindInChat={true}
        enableContinuousChat={true}
        onClose={toggleCoach}
        additionalStyles={style}
        data-testid="EnterpriseAdminCoach"
      />
    </ErrorBoundaryWithTagging>
  );
};

export default AdminCoach;
